import { useMemo, useState } from "react";
import { Link } from "wouter";
import { CalendarDays, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VetoControls } from "./agentic-ui";
import { channelConnected, connectionName, policyChannelOf, publishAfterOf, vetoStateOf } from "./agentic";
import { awaitingQaOf } from "./quality";
import { EmptyState, ItemRow, PublishActions, SectionHeading } from "./item-ui";
import { itemPlatformConnected } from "./use-studio";
import {
  dayKey,
  fmtDate,
  fmtDateTime,
  fmtDayHeading,
  fmtTime,
  isApproved,
  isFailed,
  isPending,
  isPublishable,
  isPublished,
  isRejected,
  kindOf,
  platformLabel,
  toDate,
  type StudioItem,
} from "./helpers";

const DAY_MS = 86_400_000;
const LIST_CAP = 6;

function byScheduled(a: StudioItem, b: StudioItem) {
  return (toDate(a.scheduledAt)?.getTime() ?? 0) - (toDate(b.scheduledAt)?.getTime() ?? 0);
}

/** A list that shows the first few rows and a "Show N more" button. */
function CappedList({ items, render, cap = LIST_CAP }: { items: StudioItem[]; render: (item: StudioItem) => React.ReactNode; cap?: number }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, cap);
  return (
    <div className="space-y-2">
      {shown.map(render)}
      {items.length > cap && (
        <Button type="button" variant="ghost" size="sm" onClick={() => setAll((v) => !v)} data-testid="button-show-more">
          {all ? "Show fewer" : `Show ${items.length - cap} more`}
        </Button>
      )}
    </div>
  );
}

/** A piece set to publish by itself on a channel that is not connected will sit there; say so on the row. */
function unconnectedNote(item: StudioItem, workspace: any): string | undefined {
  const ch = policyChannelOf(item);
  if (!ch || channelConnected(workspace, ch) !== false) return undefined;
  return `${connectionName(ch)} isn't connected, so this can't go out by itself.`;
}

// ── Tab ─────────────────────────────────────────────────────────────────────

export function ReadyTab({
  items,
  loading,
  workspace,
  onOpen,
  onGoCalendar,
}: {
  items: StudioItem[];
  loading: boolean;
  workspace: any;
  onOpen: (item: StudioItem) => void;
  onGoCalendar: () => void;
}) {
  const data = useMemo(() => {
    const now = Date.now();
    const horizon = now + 14 * DAY_MS;
    const live = items.filter((i) => !isRejected(i) && !isPublished(i));
    // Passed the checks, not scheduled yet: a second reviewer has it. Without its own list it would sit in no tab until the review comes back.
    const awaitingQa = live.filter(awaitingQaOf).sort((a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0));
    // Pieces on a Veto or Automatic channel are decided by the countdown, not by a date the owner picks.
    const flowing = live
      .filter((i) => ["pending", "released"].includes(vetoStateOf(i)))
      .sort((a, b) => (publishAfterOf(a)?.getTime() ?? 0) - (publishAfterOf(b)?.getTime() ?? 0));
    const dated = live.filter((i) => vetoStateOf(i) === "none");
    const upcoming = dated
      .filter((i) => {
        const t = toDate(i.scheduledAt)?.getTime();
        return t !== undefined && t >= now && t <= horizon;
      })
      .sort(byScheduled);
    const approved = dated.filter(isApproved);
    const failed = approved.filter(isFailed);
    const overdue = approved
      .filter((i) => !isFailed(i) && (toDate(i.scheduledAt)?.getTime() ?? Infinity) < now)
      .sort((a, b) => byScheduled(b, a));
    const needsDate = approved
      .filter((i) => !isFailed(i) && !toDate(i.scheduledAt) && isPublishable(kindOf(i)))
      .sort((a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0));
    const docs = approved
      .filter((i) => !isPublishable(kindOf(i)))
      .sort((a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0));

    const grouped = new Map<string, StudioItem[]>();
    for (const item of upcoming) {
      const k = dayKey(toDate(item.scheduledAt)!);
      grouped.set(k, [...(grouped.get(k) ?? []), item]);
    }
    return { upcoming, grouped: Array.from(grouped.entries()), flowing, awaitingQa, failed, overdue, needsDate, docs, waitingCount: items.filter(isPending).length };
  }, [items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="mr-2 animate-spin" size={18} /> Loading…
      </div>
    );
  }

  // One notice instead of a warning on every row.
  const unconnected = Array.from(
    new Set(
      [...data.overdue, ...data.needsDate, ...data.upcoming, ...data.failed]
        .filter((i) => kindOf(i) === "social" && !itemPlatformConnected(workspace, i))
        .map((i) => platformLabel(i)),
    ),
  );
  const joinNames = (names: string[]) =>
    names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

  const withActions = (item: StudioItem) => (
    <ItemRow
      key={item.id}
      item={item}
      onOpen={onOpen}
      dateLabel={item.scheduledAt ? `due ${fmtDate(item.scheduledAt)}` : undefined}
      actions={<PublishActions item={item} workspace={workspace} compact />}
    />
  );

  return (
    <div className="space-y-8" data-testid="ready-tab">
      {unconnected.length > 0 && (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100" data-testid="banner-unconnected">
          {joinNames(unconnected)} {unconnected.length === 1 ? "isn't" : "aren't"} connected, so posts for {unconnected.length === 1 ? "it" : "them"} won't go
          out by themselves. Use Copy text and post by hand on the planned day, or{" "}
          <Link href="/settings" className="underline underline-offset-2">connect {unconnected.length === 1 ? "it" : "them"} in Settings</Link>.
        </p>
      )}

      {/* Passed the checks, waiting for the independent QA review */}
      {data.awaitingQa.length > 0 && (
        <section className="space-y-3" data-testid="section-awaiting-qa">
          <SectionHeading title="Waiting for independent QA" count={data.awaitingQa.length} hint="Passed the checks; a second reviewer is reading them (usually under an hour)" />
          <CappedList
            items={data.awaitingQa}
            render={(item) => <ItemRow key={item.id} item={item} onOpen={onOpen} note="Nothing publishes until independent QA passes." />}
          />
        </section>
      )}

      {/* Publishing by themselves: counting down, with Hold / Release now */}
      {data.flowing.length > 0 && (
        <section className="space-y-3" data-testid="section-flowing">
          <SectionHeading title="Publishing by themselves" count={data.flowing.length} hint="Hold anything you don't want to go out" />
          <CappedList
            items={data.flowing}
            render={(item) => (
              <ItemRow
                key={item.id}
                item={item}
                onOpen={onOpen}
                dateLabel={publishAfterOf(item) ? `goes out ${fmtDateTime(publishAfterOf(item))}` : undefined}
                note={unconnectedNote(item, workspace)}
                actions={<VetoControls item={item} size="sm" testIdSuffix="-ready" />}
              />
            )}
          />
        </section>
      )}

      {/* Next 14 days */}
      <section className="space-y-3" data-testid="next-14-days">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <SectionHeading title="Next 14 days" count={data.upcoming.length} hint="What is planned to go out" />
          <button
            type="button"
            onClick={onGoCalendar}
            className="inline-flex items-center gap-1 text-sm text-blue-400 underline-offset-2 hover:underline"
            data-testid="link-open-calendar"
            title="Month and week views, with planned slots, countdowns and what has been published"
          >
            <CalendarDays size={14} /> Open the calendar
          </button>
        </div>
        {data.grouped.length === 0 ? (
          <EmptyState title="Nothing is scheduled for the next 14 days.">
            {data.needsDate.length > 0
              ? `${data.needsDate.length} approved item${data.needsDate.length === 1 ? " is" : "s are"} waiting for a date below.`
              : data.waitingCount > 0
                ? `${data.waitingCount} item${data.waitingCount === 1 ? " is" : "s are"} waiting for your decision first.`
                : "Approve something and pick a date for it."}
          </EmptyState>
        ) : (
          <div className="space-y-4">
            {data.grouped.map(([k, dayItems]) => (
              <div key={k} className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {fmtDayHeading(new Date(`${k}T12:00:00`))}
                </h3>
                {dayItems.map((item) => (
                  <ItemRow key={item.id} item={item} onOpen={onOpen} dateLabel={fmtTime(item.scheduledAt)} />
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      {data.failed.length > 0 && (
        <section className="space-y-3" data-testid="section-failed">
          <SectionHeading title="Couldn't post" count={data.failed.length} hint="These tried to go out and failed" />
          <CappedList items={data.failed} render={withActions} />
        </section>
      )}

      {data.overdue.length > 0 && (
        <section className="space-y-3" data-testid="section-overdue">
          <SectionHeading title="Overdue — not sent" count={data.overdue.length} hint="The planned date passed and these never went out" />
          <CappedList items={data.overdue} render={withActions} />
        </section>
      )}

      {data.needsDate.length > 0 && (
        <section className="space-y-3" data-testid="section-needs-date">
          <SectionHeading title="Approved, waiting for a date" count={data.needsDate.length} />
          <CappedList items={data.needsDate} render={withActions} />
        </section>
      )}

      {data.docs.length > 0 && (
        <details className="group rounded-xl border border-border bg-card/40" data-testid="section-approved-docs">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
            <ChevronRight size={16} className="text-muted-foreground transition-transform group-open:rotate-90" />
            Approved plans, briefs, emails and ads
            <span className="font-normal text-muted-foreground">{data.docs.length} — nothing to publish</span>
          </summary>
          <div className="space-y-2 border-t border-border p-3 sm:p-4">
            {data.docs.map((item) => (
              <ItemRow key={item.id} item={item} onOpen={onOpen} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
